import { useState, type FormEvent } from "react";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, LockKeyhole, ShieldCheck } from "lucide-react";
import { useSession } from "../context/session";
import { ApiError } from "../lib/api";
import { Button, Field, Loading, Tag } from "../components/ui";

export function EntryPage() {
  const { user, config, loading } = useSession();
  if (loading) return <PublicLoading />;
  if (user) return <Navigate to="/console/links" replace />;
  if (config?.registrationOpen && !config.maintenanceMode) return <Navigate to="/login" replace />;
  return <Navigate to="/maintenance" replace />;
}

function PublicLoading() {
  return (
    <main className="public-shell public-shell--center">
      <Loading label="正在连接短域名系统" />
    </main>
  );
}

function AuthAside() {
  const { config } = useSession();
  return (
    <aside className="auth-aside">
      <span className="auth-aside__index">G2 / AL</span>
      <div className="auth-aside__statement">
        <span className="eyebrow">ONE CHARACTER · CONTROLLED ACCESS</span>
        <h1>把长地址，<br />压进一个清楚的路径。</h1>
        <p>短链创建、域名审核、到期释放与点击统计，在同一套规则里运行。</p>
      </div>
      <div className="auth-aside__rules">
        <div><strong>{config?.userSlugLength ?? 6}</strong><span>用户后缀起始长度</span></div>
        <div><strong>{config?.defaultUserTtlDays ?? 90}d</strong><span>默认有效期</span></div>
        <div><strong>2×</strong><span>规则与审核双保险</span></div>
      </div>
    </aside>
  );
}

export function LoginPage() {
  const { user, config, login } = useSession();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  if (user) return <Navigate to="/console/links" replace />;

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setPending(true);
    setError(null);
    try {
      await login({ email, password });
      navigate("/console/links", { replace: true });
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "登录没有完成");
    } finally {
      setPending(false);
    }
  };

  return (
    <main className="auth-page">
      <AuthAside />
      <section className="auth-form-panel">
        <header className="auth-form-panel__top">
          <Link to="/" className="wordmark">{config?.appName ?? "g2.al"}</Link>
          <Tag tone="outline">{config?.registrationOpen ? "PUBLIC BETA" : "INTERNAL"}</Tag>
        </header>
        <form className="auth-form" onSubmit={(event) => void submit(event)}>
          <span className="eyebrow">ACCOUNT · LOG IN</span>
          <h2>登录控制台</h2>
          <p>使用已获准的邮箱进入。初始管理员由部署环境变量创建。</p>
          <Field label="邮箱 · EMAIL">
            <input className="input input--large" type="email" autoComplete="email" required value={email} onChange={(event) => setEmail(event.target.value)} placeholder="name@example.com" autoFocus />
          </Field>
          <Field label="密码 · PASSWORD">
            <input className="input input--large" type="password" autoComplete="current-password" required value={password} onChange={(event) => setPassword(event.target.value)} placeholder="••••••••••" />
          </Field>
          {error ? <div className="inline-error" role="alert">{error}</div> : null}
          {!config?.hasAdmin ? (
            <div className="inline-note"><LockKeyhole size={16} /><span>系统尚未发现超级管理员；第一次登录前请完成部署环境配置。</span></div>
          ) : null}
          <Button variant="primary" className="button--block button--large" type="submit" disabled={pending}>
            {pending ? "正在验证…" : "登录"}<ArrowRight size={16} />
          </Button>
          {config?.registrationOpen ? <p className="auth-switch">还没有账号？<Link to="/register">创建账号 →</Link></p> : null}
        </form>
        <footer className="auth-form-panel__footer"><ShieldCheck size={15} />会话使用 HttpOnly Cookie，API 使用可撤销令牌。</footer>
      </section>
    </main>
  );
}

export function RegisterPage() {
  const { user, config, register } = useSession();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  if (user) return <Navigate to="/console/links" replace />;
  if (config && !config.registrationOpen) return <Navigate to="/maintenance" replace />;

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (password !== confirm) {
      setError("两次输入的密码不一致");
      return;
    }
    setPending(true);
    setError(null);
    try {
      await register({ email, password });
      navigate("/console/links", { replace: true });
    } catch (reason) {
      setError(reason instanceof ApiError || reason instanceof Error ? reason.message : "注册没有完成");
    } finally {
      setPending(false);
    }
  };

  return (
    <main className="auth-page">
      <AuthAside />
      <section className="auth-form-panel">
        <header className="auth-form-panel__top">
          <Link to="/" className="wordmark">{config?.appName ?? "g2.al"}</Link>
          <Tag tone="accent">SIGN UP</Tag>
        </header>
        <form className="auth-form" onSubmit={(event) => void submit(event)}>
          <span className="eyebrow">ACCOUNT · CREATE</span>
          <h2>创建账号</h2>
          <p>
            注册后可创建 {config?.userSlugLength ?? 6} 位随机后缀短链，默认有效期 {config?.defaultUserTtlDays ?? 90} 天。
          </p>
          <Field label="邮箱 · EMAIL" hint={config?.allowedEmailDomains.length ? `允许：${config.allowedEmailDomains.join("、")}` : "当前接受所有有效邮箱域名。"}>
            <input className="input input--large" type="email" autoComplete="email" required value={email} onChange={(event) => setEmail(event.target.value)} autoFocus />
          </Field>
          <Field label="密码 · PASSWORD" hint="至少 10 位。建议使用密码管理器生成。">
            <input className="input input--large" type="password" autoComplete="new-password" minLength={10} required value={password} onChange={(event) => setPassword(event.target.value)} />
          </Field>
          <Field label="确认密码 · CONFIRM">
            <input className="input input--large" type="password" autoComplete="new-password" minLength={10} required value={confirm} onChange={(event) => setConfirm(event.target.value)} />
          </Field>
          {error ? <div className="inline-error" role="alert">{error}</div> : null}
          <Button variant="primary" className="button--block button--large" type="submit" disabled={pending}>
            {pending ? "正在创建…" : "创建并登录"}<ArrowRight size={16} />
          </Button>
          <p className="auth-switch">已有账号？<Link to="/login">登录 →</Link></p>
        </form>
      </section>
    </main>
  );
}

export function MaintenancePage() {
  const { user, config } = useSession();
  if (user && (!config?.maintenanceMode || user.role !== "user")) return <Navigate to="/console/links" replace />;
  return (
    <main className="maintenance-page">
      <header className="maintenance-nav">
        <strong className="wordmark wordmark--inverse">{config?.appName ?? "g2.al"}</strong>
        <Link to="/login">内部登录 <ArrowRight size={14} /></Link>
      </header>
      <section className="maintenance-statement">
        <div className="maintenance-statement__index">PRIVATE / 01</div>
        <div className="maintenance-statement__copy">
          <span className="eyebrow eyebrow--inverse">SHORT DOMAIN · CONTROLLED ROLLOUT</span>
          <h1>一个字符，<br />足够抵达。</h1>
          <p>{config?.appName ?? "g2.al"} 正在内部运行。开放后将限定邮箱域名、启用目标站白名单审核，并让每个后缀拥有明确的有效期与释放策略。</p>
        </div>
      </section>
      <section className="maintenance-grid">
        <div><span>01 · RESERVE</span><strong>极短号池</strong><p>管理员维护稀缺的数字与短字符后缀。</p></div>
        <div><span>02 · REVIEW</span><strong>域名审核</strong><p>白名单直通，其余链接进入清晰的审核队列。</p></div>
        <div><span>03 · RELEASE</span><strong>到期释放</strong><p>宽限、冷却、回收都有可见的时间边界。</p></div>
      </section>
      <footer className="maintenance-footer"><span>STATUS · INTERNAL</span><span>© {new Date().getFullYear()} {config?.appName ?? "g2.al"}</span></footer>
    </main>
  );
}

export function UnavailablePage() {
  const [search] = useSearchParams();
  const slug = search.get("slug") ?? "?";
  const reason = search.get("reason") ?? "not-found";
  const messages: Record<string, string> = {
    "not-found": "这个后缀还没有被分配。",
    pending: "这条短链正在等待审核。",
    rejected: "这条短链没有通过审核。",
    grace: "这条短链已进入宽限期。",
    released: "这条短链已释放，不再保存原目标。",
    disabled: "这条短链已被停用。",
  };
  return (
    <main className="unavailable-page">
      <header><Link to="/" className="wordmark">g2.al</Link><Tag tone="outline">NO ROUTE</Tag></header>
      <section>
        <span className="eyebrow">HTTP · LINK UNAVAILABLE</span>
        <div className="unavailable-slug">/{slug}</div>
        <h1>没有可用的跳转。</h1>
        <p>{messages[reason] ?? "这条短链当前不可用。"}</p>
        <Link className="button button--secondary" to="/"><ArrowLeft size={15} />返回首页</Link>
      </section>
    </main>
  );
}
